/**
 * Computed Service - On-demand calculations with optional caching
 *
 * Supports:
 * - Read all (computed on-demand)
 * - Read by ID
 * - Source aggregation with resolver (absorbed from VirtualService)
 * - LRU caching with TTL
 * - Reactive invalidation via events
 */

import type {
  ComputedEntityDefinition,
  PaginatedResult,
  ModuleContext,
  EntityServiceHooks,
  ManagedCache
} from '@gzl10/nexus-sdk'
import { resolveLocalized, entityRoom } from '@gzl10/nexus-sdk'
import type { EntityQuery, EntityService } from '@kernel/runtime/index.js'
import { BaseEntityService } from './base.service.js'

export class ComputedService<T extends Record<string, unknown> = Record<string, unknown>>
  extends BaseEntityService<T> {

  declare readonly definition: ComputedEntityDefinition
  private cache: ManagedCache<unknown[]>

  constructor(ctx: ModuleContext, definition: ComputedEntityDefinition, hooks?: EntityServiceHooks<T>, repo?: import('../repositories/types.js').EntityRepository) {
    super(ctx, definition, hooks, repo)

    // Shared cache across all computed entities, with per-entity invalidation rules
    this.cache = ctx.core.cache.getOrCreate<unknown[]>('runtime:computed', {
      maxEntries: 200,
      defaultTTL: definition.cache?.ttl ?? 60
    })

    if (definition.cache?.invalidateOn?.length) {
      this.cache.addInvalidationRules(definition.cache.invalidateOn)
    }

    // Register live mode observer: recompute and push when sources change
    if (definition.realtime === 'live' && definition.cache?.invalidateOn?.length) {
      this.registerLiveObserver(definition.cache.invalidateOn)
    }

    // Warm cache on startup if configured
    if (definition.warmCache) {
      this.compute().catch(err => {
        this.logger.warn({ err, entity: resolveLocalized(this.definition.label, 'en') }, 'Cache warm failed')
      })
    }
  }

  /**
   * Register observer for live mode: when invalidation events fire,
   * recompute and push entity.updated to subscribed clients.
   */
  private registerLiveObserver(events: string[]): void {
    // Lazy import to avoid circular deps (socket.ts imports from core)
    import('@kernel/events/socket.js').then(({ getRoomSize, isSocketIOInitialized }) => {
      const handler = async () => {
        if (!isSocketIOInitialized()) return

        const room = entityRoom(this.ctx.tenantId, this.moduleName, this.entityKey)
        const roomSize = await getRoomSize(room)
        if (roomSize === 0) return

        this.logger.debug({ entity: this.definition.label, room, roomSize }, 'Live recompute triggered')

        try {
          await this.clearCache()

          this.ctx.core.events.emit('entity.updated', {
            tenantId: this.ctx.tenantId,
            module: this.moduleName,
            entity: this.entityKey,
            action: 'updated' as const,
            id: '*',
            data: undefined,
            timestamp: new Date().toISOString()
          })
        } catch (err) {
          this.logger.error({ err, entity: this.definition.label }, 'Live recompute failed')
        }
      }

      // Debounced listener for each invalidation event
      for (const event of events) {
        let timer: ReturnType<typeof setTimeout> | null = null
        ;(this.ctx.core.events as unknown as { on: (e: string, fn: () => void) => void }).on(event, () => {
          if (timer) clearTimeout(timer)
          timer = setTimeout(handler, 500)
        })
      }
    })
  }

  /**
   * Get table - computed entities don't have a local table
   */
  protected override get table(): string {
    throw new Error('Computed entities do not have a local table')
  }

  /**
   * Get cache key
   */
  private getCacheKey(params?: Record<string, unknown>): string {
    const base = `computed:${this.moduleName}.${resolveLocalized(this.definition.label, 'en')}`
    if (!params || Object.keys(params).length === 0) {
      return base
    }
    return `${base}:${JSON.stringify(params)}`
  }

  /**
   * Fetch data from source services and apply resolver (absorbed from VirtualService)
   */
  private async computeFromSources(): Promise<unknown[]> {
    const sources = this.definition.sources!
    const data: Record<string, unknown[]> = {}

    await Promise.all(
      sources.map(async (sourceName) => {
        const service = this.ctx.services.getOptional<EntityService>(sourceName)
        if (service) {
          try {
            const result = await service.findAll()
            data[sourceName] = result.items
          } catch (err) {
            this.logger.error({ source: sourceName, err }, 'Failed to fetch source data')
            data[sourceName] = []
          }
        } else {
          this.logger.warn({ source: sourceName }, 'Source service not found')
          data[sourceName] = []
        }
      })
    )

    if (this.definition.resolver) {
      const resolved = this.definition.resolver(data, this.ctx)
      return resolved instanceof Promise ? await resolved : resolved
    }

    // Default: flat merge all sources
    return Object.values(data).flat()
  }

  /**
   * Execute compute function or source aggregation
   */
  private async compute(params?: Record<string, unknown>): Promise<unknown[]> {
    // Check cache first (applies to both compute and sources modes)
    const cacheKey = this.getCacheKey(params)
    const cached = await this.cache.get(cacheKey)
    if (cached) {
      this.ctx.core.logger.debug({ entity: this.definition.label }, 'Computed entity cache hit')
      return cached
    }

    let result: unknown[]

    // Source-based computation (absorbed from virtual entity type)
    if (this.definition.sources?.length) {
      result = await this.computeFromSources()
    } else if (this.definition.compute) {
      // Function-based computation (original behavior)
      this.ctx.core.logger.debug({ entity: this.definition.label }, 'Computing entity data')
      try {
        const computed = await this.definition.compute(this.ctx, params)

        if (!Array.isArray(computed)) {
          this.ctx.core.logger.error(
            { entity: this.definition.label, result: typeof computed },
            'Compute function did not return an array'
          )
          return []
        }

        result = computed
      } catch (error) {
        this.ctx.core.logger.error(
          { entity: this.definition.label, error },
          'Failed to compute entity data'
        )
        throw error
      }
    } else {
      throw new this.errors.AppError(
        `No compute function or sources defined for ${resolveLocalized(this.definition.label, 'en')}`,
        500
      )
    }

    this.ctx.core.logger.info(
      { entity: this.definition.label, count: result.length },
      'Computed entity data'
    )

    // Cache result with entity-specific TTL
    const ttl = this.definition.cache?.ttl
    if (ttl && ttl > 0) {
      await this.cache.set(cacheKey, result, ttl)
    }

    return result
  }

  /**
   * Get all computed items with pagination
   */
  async findAll(query?: EntityQuery): Promise<PaginatedResult<T>> {
    // Compute data - pass filters so compute function can optimize if needed
    const items = await this.compute(query?.filters)

    // Apply in-memory filters, search, sort, and pagination
    return this.inMemoryFindAll(items, query)
  }

  // applyInMemoryFilters inherited from BaseEntityService with full operator support

  /**
   * Get computed item by ID
   */
  async findById(id: string): Promise<T | null> {
    // For source-based entities, search in sources directly (more efficient)
    if (this.definition.sources?.length && !this.definition.compute) {
      for (const sourceName of this.definition.sources) {
        const service = this.ctx.services.getOptional<EntityService>(sourceName)
        if (service) {
          try {
            const item = await service.findById(id)
            if (item) return this.processAfterFindById(item as T)
          } catch { /* continue to next source */ }
        }
      }
      return null
    }

    // Original: search in computed results
    const items = await this.compute()
    const item = items.find(i => (i as Record<string, unknown>)['id'] === id)
    return this.processAfterFindById((item as T) ?? null)
  }

  /**
   * Recompute with specific params
   */
  async recompute(params?: Record<string, unknown>): Promise<T[]> {
    // Clear cache for this computation
    const cacheKey = this.getCacheKey(params)
    await this.cache.delete(cacheKey)

    return this.compute(params) as Promise<T[]>
  }

  /**
   * Clear all cache for this entity
   */
  async clearCache(): Promise<void> {
    const prefix = `computed:${this.moduleName}.${resolveLocalized(this.definition.label, 'en')}`
    await this.cache.deleteByPrefix(prefix)
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return this.cache.getStats()
  }

  /**
   * Computed entities are read-only
   */
  async create(_data: Partial<T>): Promise<T> {
    throw new this.errors.ForbiddenError('Computed entities are read-only')
  }

  async update(_id: string, _data: Partial<T>): Promise<T> {
    throw new this.errors.ForbiddenError('Computed entities are read-only')
  }

  async delete(_id: string): Promise<void> {
    throw new this.errors.ForbiddenError('Computed entities are read-only')
  }
}
